import { CalendarEvent, EVENT_TYPE_COLORS, EVENT_TYPE_ICONS, EVENT_TYPE_LABELS } from '../../../hooks/useCalendarEvents';
import { StatusDot } from './EventBadge';

interface CalendarListViewProps {
  year: number;
  month: number;
  eventsByDate: Record<string, CalendarEvent[]>;
  onEventClick: (event: CalendarEvent) => void;
  onAddDay: (date: string) => void;
}

const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const WEEKDAY_NAMES = ['domingo','segunda','terça','quarta','quinta','sexta','sábado'];

export const CalendarListView = ({
  year, month, eventsByDate, onEventClick, onAddDay,
}: CalendarListViewProps) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const daysWithEvents = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    const dateStr = `${year}-${pad(month)}-${pad(d)}`;
    const events = eventsByDate[dateStr] ?? [];
    return { day: d, dateStr, events };
  }).filter((x) => x.events.length > 0);

  if (daysWithEvents.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
          <i className="ri-calendar-2-line text-2xl text-gray-400" />
        </div>
        <h3 className="text-[15px] font-600 text-gray-700 mb-1">
          Nenhum evento em {MONTH_NAMES[month - 1]}
        </h3>
        <p className="text-[13px] text-gray-400 max-w-xs">
          Os lançamentos pendentes, vencimentos de cartão e metas aparecerão aqui automaticamente.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {daysWithEvents.map(({ day, dateStr, events }) => {
        const dt = new Date(dateStr + 'T00:00:00');
        const weekday = WEEKDAY_NAMES[dt.getDay()];
        const isToday = dateStr === todayStr;
        const totalIncome = events
          .filter((e) => e.event_type === 'income_expected' || e.event_type === 'income_received')
          .reduce((s, e) => s + (e.amount ?? 0), 0);
        const totalExpense = events
          .filter((e) => e.event_type !== 'income_expected' && e.event_type !== 'income_received' && e.amount != null)
          .reduce((s, e) => s + (e.amount ?? 0), 0);

        return (
          <div key={dateStr} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div
              className={`flex items-center justify-between px-4 py-3 border-b border-gray-50 ${
                isToday ? 'bg-emerald-50/60' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-700 text-[15px] ${
                  isToday ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700'
                }`}>
                  {day}
                </div>
                <div>
                  <span className="text-[13px] font-600 text-gray-700 capitalize">{weekday}</span>
                  {isToday && <span className="ml-2 text-[11px] text-emerald-600 font-500">Hoje</span>}
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-[11px] text-gray-400">
                      {events.length} evento{events.length > 1 ? 's' : ''}
                    </p>
                    {totalIncome > 0 && (
                      <span className="text-[11px] text-emerald-600 font-500">
                        +{totalIncome.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                    )}
                    {totalExpense > 0 && (
                      <span className="text-[11px] text-red-500 font-500">
                        -{totalExpense.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => onAddDay(dateStr)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-emerald-100 hover:text-emerald-500 transition-colors cursor-pointer"
                title="Adicionar evento neste dia"
              >
                <i className="ri-add-line text-[15px]" />
              </button>
            </div>

            <div className="p-3 space-y-1.5">
              {events.map((evt) => {
                const icon = EVENT_TYPE_ICONS[evt.event_type] ?? 'ri-calendar-line';
                const color = evt.color || EVENT_TYPE_COLORS[evt.event_type];
                const typeLabel = EVENT_TYPE_LABELS[evt.event_type] ?? 'Evento';
                const isIncome = evt.event_type === 'income_expected' || evt.event_type === 'income_received';

                return (
                  <button
                    key={evt.id}
                    onClick={() => onEventClick(evt)}
                    className="w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all cursor-pointer hover:brightness-95 group"
                    style={{ borderColor: color + '30', backgroundColor: color + '08' }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: color + '20' }}
                    >
                      <i className={`${icon} text-[15px]`} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[13px] font-600 text-gray-800 truncate">{evt.title}</span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-500 shrink-0"
                          style={{ backgroundColor: color + '20', color }}
                        >
                          {typeLabel}
                        </span>
                        {evt.is_manual && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-500 font-500 shrink-0">
                            Manual
                          </span>
                        )}
                        {evt.sync_with_google && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-400 font-500 shrink-0 flex items-center gap-0.5">
                            <i className="ri-google-line text-[9px]" />G
                          </span>
                        )}
                      </div>
                      {evt.description && (
                        <p className="text-[12px] text-gray-400 truncate mt-0.5">{evt.description}</p>
                      )}
                    </div>
                    {evt.amount != null && (
                      <span className="text-[13px] font-700 shrink-0" style={{ color }}>
                        {isIncome ? '+' : '-'}
                        {Number(evt.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    )}
                    <StatusDot event={evt} size="md" />
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-gray-500 text-[16px] shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
